import { type CSSProperties, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { Mark } from '@selflevel/ui';
import { IconAjustes } from './iconos';
import { useTecladoAbierto } from './teclado';
import { casa, indiceActivo, PRINCIPALES, SECUNDARIOS } from './destinos';

/**
 * Las dos expresiones de la misma estructura, y ninguna es la versión reducida
 * de la otra. Qué significa cada sección y qué cuelga de cuál está en
 * `destinos.ts`, que no pinta nada y por eso se puede probar.
 *
 *   · MÓVIL      barra inferior de cinco, con la pieza acuñada que se desliza.
 *   · ESCRITORIO riel lateral con los cinco arriba, los secundarios debajo del
 *                filete y Ajustes al fondo. En escritorio hay sitio: esconder
 *                cosas ahí no es contención, es esconderlas.
 */

/** `--i` mueve la pieza; `--n` dice entre cuántas se reparte el ancho. */
function pista(i: number, n: number): CSSProperties {
  return { ['--i']: i, ['--n']: n } as CSSProperties;
}

/**
 * LA BARRA INFERIOR (móvil): cápsula flotante de cristal.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Fue una losa a sangre con una joroba recortada y un disco de ámbar que
 * asomaba por el canto. Ahora es una cápsula que flota, con los destinos
 * inactivos en solo icono y el activo desplegando su rótulo. El porqué: una
 * losa pegada al borde se lee como mueble del sistema —la barra del navegador,
 * la del teléfono— y una cápsula separada se lee como la app.
 *
 * Aquí no hay nada que colocar ni que medir, y por eso este componente no tiene
 * ni una ref ni un efecto: la pastilla del activo ES el enlace activo, así que
 * la pinta el atributo `data-active` y la coloca el propio flujo. La versión
 * anterior necesitaba dos propiedades personalizadas para mover una pieza
 * absoluta; esta no necesita ninguna.
 *
 * El detalle que no se ve y sostiene la accesibilidad está anotado abajo, en el
 * rótulo: no se quita del árbol, se recorta.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function BarraInferior({ pathname }: { pathname: string }): ReactElement {
  const activo = indiceActivo(pathname);
  // Como en una app nativa: mientras se escribe, la barra se aparta del teclado.
  const teclado = useTecladoAbierto();

  return (
    <nav className="nav" aria-label="Navegación principal" data-teclado={teclado}>
      {PRINCIPALES.map(({ to, label, Icon }, i) => (
        <Link
          key={to}
          to={to}
          className="nav__item"
          data-active={i === activo}
          {...(i === activo ? { 'aria-current': 'page' as const } : {})}
        >
          <span className="nav__glifo">
            <Icon size={24} />
          </span>
          {/* El rótulo NO se quita del árbol cuando el destino no está activo:
              se recorta a cero de ancho. La diferencia importa — con
              `display: none` un lector de pantalla anunciaría cinco enlaces sin
              nombre; recortado, el texto sigue ahí y se lee igual que siempre.
              Por eso tampoco hace falta un `aria-label` de repuesto. */}
          <span className="nav__label">
            <span className="nav__label-txt">{label}</span>
          </span>
        </Link>
      ))}
    </nav>
  );
}

/**
 * EL RIEL (escritorio).
 *
 * Va sin cristal, a propósito. La ley de capas del paso 3 permite cristal en la
 * navegación, pero un panel de 260 × 100 vh con `backdrop-filter` vivo es la
 * superficie más cara de la pantalla y está permanentemente a la vista: cae de
 * lleno en la regla de los tres segundos —lo que se mira más de tres segundos
 * seguidos va sólido—. Aquí el riel es transparente y se apoya en el lienzo;
 * lo único que se dibuja es el filete de la derecha y la pieza del destino
 * activo. Es también lo que hace que la cabecera de cada pantalla siga siendo
 * el único cristal vivo en escritorio, igual que en móvil.
 */
export function Riel({ pathname }: { pathname: string }): ReactElement {
  const activo = indiceActivo(pathname);

  return (
    <nav className="riel" aria-label="Navegación principal">
      <Link to="/" className="riel__marca">
        <Mark size={26} />
        <span className="riel__nombre">selflevel</span>
      </Link>

      <div className="riel__grupo" style={pista(activo, PRINCIPALES.length)}>
        <span className="riel__coin" aria-hidden="true" />
        {PRINCIPALES.map(({ to, label, Icon }, i) => (
          <Link
            key={to}
            to={to}
            className="riel__item"
            data-active={i === activo}
            {...(i === activo ? { 'aria-current': 'page' as const } : {})}
          >
            <Icon size={20} />
            <span className="grow">{label}</span>
          </Link>
        ))}
      </div>

      <p className="riel__rotulo t-caption t-dim">Además</p>

      <div className="riel__grupo riel__grupo--secundario">
        {SECUNDARIOS.map(({ to, label, Icon, nota }) => (
          <Link key={to} to={to} className="riel__item" data-active={casa(pathname, to)}>
            <Icon size={20} />
            <span className="grow">
              {label}
              <span className="riel__nota">{nota}</span>
            </span>
          </Link>
        ))}
      </div>

      <Link to="/ajustes" className="riel__item riel__pie" data-active={casa(pathname, '/ajustes')}>
        <IconAjustes size={20} />
        <span className="grow">Ajustes</span>
      </Link>
    </nav>
  );
}
